import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import AppCell from '@/components/common/app-cell';
import Toggle from '@/components/common/toggle';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import usePrivacySettings from '@/hooks/settings/use-privacy-settings';
import useUpdatePrivacySettings from '@/hooks/settings/use-update-privacy-settings';
import useUserPlatform from '@/hooks/use-user-platform';
import type { PrivacySettings } from '@/models/mine/privacy-settings';
import ROUTES from '@/routes';
import { useEmbedLayout } from '@/stores/device';
import { EmbedLayout } from '@/utils/embed-layout';

export default function PrivacySettingsRoute() {
  const { t } = useTranslation();
  const { data: privacySettings } = usePrivacySettings();
  const { updatePrivacySettings, isPending } = useUpdatePrivacySettings();
  const { isDesktop } = useUserPlatform();
  const { dispatch } = useCommDispatcher();
  const layout = useEmbedLayout();
  const [searchParams] = useTypedSearchParams(ROUTES.MY.SETTINGS.PRIVACY);

  const handleUpdatePrivacySetting = (key: keyof PrivacySettings) => (value: boolean) => {
    updatePrivacySettings(key, value);
  };

  return (
    <PageLayout
      className='mode-embedded:bg-neutral-100'
      title={t('mine.privacy')}
      headerConfig={{
        showHeader: layout >= EmbedLayout.COMPACT,
        navigateBack: !isDesktop,
        className: 'mode-embedded:text-primary-content',
        onNavigateBack: () => (searchParams.from === 'onboard' ? dispatch({ event: 'dismiss' }) : undefined),
      }}>
      <div className='desktop:mx-1 flex flex-col mt-4 mode-embedded:mx-5 mode-embedded:bg-neutral-0 mode-embedded:rounded-md'>
        <AppCell
          label={t('privacy.verify-on-friend')}
          suffix={
            <Toggle
              isLoading={isPending}
              checked={privacySettings?.IsManualVerifiedFriend ?? false}
              onChange={handleUpdatePrivacySetting('IsManualVerifiedFriend')}
            />
          }
        />
        <AppCell
          label={t('privacy.verify-on-add-group')}
          suffix={
            <Toggle
              isLoading={isPending}
              checked={privacySettings?.IsManualVerifiedGroup ?? false}
              onChange={handleUpdatePrivacySetting('IsManualVerifiedGroup')}
            />
          }
        />

        <Link to={ROUTES.MY.BLACKLIST.$path()}>
          <AppCell
            hoverable
            label={t('privacy.blacklist')}
            chevron
          />
        </Link>
      </div>
    </PageLayout>
  );
}
